import LegalPageLayout from "./LegalPageLayout";

const SUMMARY_POINTS = [
  ["What personal information do we process?", "When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the choices you make."],
  ["Do we process any sensitive personal information?", "We do not process sensitive personal information."],
  ["Do we collect any information from third parties?", "We do not collect any information from third parties."],
  ["How do we process your information?", "We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law."],
  ["How do we keep your information safe?", "We have organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet can be guaranteed to be 100% secure."],
  ["What are your rights?", "Depending on where you are located, you may have certain rights regarding your personal information."],
  ["How do you exercise your rights?", "By submitting a data subject access request or by contacting us at hello@sosta.ai."],
];

export default function PrivacyPage() {
  return (
    <LegalPageLayout
      title="PRIVACY POLICY"
      subtitle="Last updated April 30, 2026"
      pageTitle="Privacy Policy — Sosta"
    >
      <p>
        This Privacy Notice for Sosta Inc. (doing business as Sosta) ("we," "us," or "our") describes how and why we might access, collect, store, use, and/or share ("process") your personal information when you use our services ("Services"), including when you:
      </p>
      <ul>
        <li>Download and use our mobile application (Sosta)</li>
        <li>Use Sosta — a breakup support app for women who are done spiraling and ready to heal. Combining an AI best friend, a no-contact tracker, video content, and a community of girls going through the same thing — Sosta is the support system you wish you had on your worst nights.</li>
        <li>Engage with us in other related ways, including any marketing or events</li>
      </ul>
      <p>
        Questions or concerns? Reading this Privacy Notice will help you understand your privacy rights and choices. If you do not agree with our policies and practices, please do not use our Services. If you still have any questions or concerns, please contact us at <a href="mailto:hello@sosta.ai">hello@sosta.ai</a>.
      </p>

      <h2>SUMMARY OF KEY POINTS</h2>
      {SUMMARY_POINTS.map(([question, answer]) => (
        <p key={question}>
          <strong>{question}</strong> {answer}
        </p>
      ))}

      <h2>1. WHAT INFORMATION DO WE COLLECT?</h2>
      <p>
        We collect personal information that you voluntarily provide to us when you register on the Services, express an interest in obtaining information about us or our products and Services, or otherwise when you contact us. This includes: names, email addresses, usernames, and passwords. We also collect payment data necessary to process your payment, which is handled by our payment processor. We may provide you with the option to register using your existing social media account details.
      </p>
      <p>
        We also automatically collect certain information when you visit or use our Services, such as your IP address, browser and device characteristics, operating system, and other technical information.
      </p>

      <h2>2. HOW DO WE PROCESS YOUR INFORMATION?</h2>
      <p>
        We process your personal information to: facilitate account creation and authentication, deliver our services to you, enable user-to-user communications, request feedback, send marketing and promotional communications (with your consent), and to save or protect an individual's vital interest.
      </p>

      <h2>3. WHAT LEGAL BASES DO WE RELY ON?</h2>
      <p>
        We only process your personal information when we believe it is necessary and we have a valid legal reason to do so, such as with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill our legitimate business interests.
      </p>

      <h2>4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</h2>
      <p>
        We may share information in connection with a merger, sale, or acquisition of our business. When you share personal information in public areas of the Services (such as community posts), such information may be viewed by all users.
      </p>

      <h2>5. DO WE OFFER ARTIFICIAL INTELLIGENCE-BASED PRODUCTS?</h2>
      <p>
        Yes. We offer AI-powered features through third-party service providers including Anthropic. Your input, output, and personal information will be shared with and processed by these AI Service Providers to enable your use of our AI features. You must not use the AI Products in any way that violates the terms or policies of any AI Service Provider.
      </p>

      <h2>6. HOW DO WE HANDLE YOUR SOCIAL LOGINS?</h2>
      <p>
        If you register or log in using a social media account (such as Apple or Google), we will receive certain profile information from your social media provider, such as your name and email address. We will use this information only for the purposes described in this Privacy Notice.
      </p>

      <h2>7. HOW LONG DO WE KEEP YOUR INFORMATION?</h2>
      <p>
        We will only keep your personal information for as long as it is necessary — generally for the period of time in which users have an account with us. When we have no ongoing legitimate business need to process your personal information, we will delete or anonymize it.
      </p>

      <h2>8. HOW DO WE KEEP YOUR INFORMATION SAFE?</h2>
      <p>
        We have implemented appropriate technical and organizational security measures to protect your personal information. However, no electronic transmission over the Internet can be guaranteed to be 100% secure. You should only access the Services within a secure environment.
      </p>

      <h2>9. DO WE COLLECT INFORMATION FROM MINORS?</h2>
      <p>
        We do not knowingly collect data from or market to children under 18 years of age. By using the Services, you represent that you are at least 18 years old. If we learn that personal information from users under 18 has been collected, we will deactivate the account and delete such data. If you become aware of any such data, please contact us at <a href="mailto:hello@sosta.ai">hello@sosta.ai</a>.
      </p>

      <h2>10. WHAT ARE YOUR PRIVACY RIGHTS?</h2>
      <p>
        Depending on your location, you may have the right to access, correct, or delete your personal information, withdraw your consent, and more. To exercise these rights, submit a data subject access request or email us at <a href="mailto:hello@sosta.ai">hello@sosta.ai</a>.
      </p>
      <p>
        You can unsubscribe from marketing communications at any time by replying "STOP" or "UNSUBSCRIBE" or by contacting us directly.
      </p>
      <p>
        To review or terminate your account, log in to your account settings and update your user account. Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases.
      </p>

      <h2>11. CONTROLS FOR DO-NOT-TRACK FEATURES</h2>
      <p>
        Most web browsers include a Do-Not-Track ("DNT") feature. We do not currently respond to DNT browser signals as no uniform technology standard has been finalized. California law requires us to let you know how we respond to web browser DNT signals — we do not respond to them at this time.
      </p>

      <h2>12. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</h2>
      <p>
        If you are a resident of California, Colorado, Connecticut, Delaware, Florida, or other applicable states, you may have the right to request access to, correct, or delete your personal information. We have not sold or shared any personal information to third parties for a business or commercial purpose in the preceding twelve months and will not do so in the future.
      </p>

      <h2>13. DO WE MAKE UPDATES TO THIS NOTICE?</h2>
      <p>
        Yes, we will update this notice as necessary to stay compliant with relevant laws. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.
      </p>

      <h2>14. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</h2>
      <p>
        If you have questions or comments about this notice, you may email us at <a href="mailto:hello@sosta.ai">hello@sosta.ai</a> or contact us by post at:
      </p>
      <p>
        Sosta Inc.<br />
        [Insert Mailing Address]
      </p>

      <h2>15. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?</h2>
      <p>
        Based on applicable laws, you may have the right to request access to the personal information we collect from you, correct inaccuracies, or delete your personal information. To request this, please fill out and submit a data subject access request.
      </p>
    </LegalPageLayout>
  );
}
